import { useEffect, useRef, useState } from "react";
import { AbsDropDown, Button } from "abs-ui-components";
import { MDBCol, MDBRow } from "mdbreact";
import axiosHttp from "../../../services/interceptor";
import _, { update } from "lodash";
import NonBlendFuelRegistryForm from "./NonBlendFuelRegistryForm";
import BlendChildFuelAccordion from "./BlendChildFuelAccordian";

import { emitToasterMsg } from "../../../services/toasterUtility";
import { callApi } from "../../../services/MonitoringPlanService";
import { calcCo2Eq, calcLcvs, calcParentCo2s, calcParentMass } from "./BlendCalculations";
import { blendFuelRegistryFieldConfigs, BLENDREQUIREDFIELDS, FUEL_CATEGORIES, FUEL_CLASSIFICATIONS, fuelRegistryFieldConfigs,BLEND_PARENT_META_KEYS } from "./Fields/FieldConfig";

const FuelRegistryForm = ({ onSuccess, onClose, fuelRegId = null, vesselMstId = 43090 }) => {
    const [classification, setClassification] = useState("Fossil");
    const [baseFuels, setBaseFuels] = useState([]);
    const [isBaseFuelsLoaded, setIsBaseFuelsLoaded] = useState(false);
    const [blendCode, setBlendCode] = useState("");
    const hasRequestedBlendCode = useRef(false);
    const [blendCategories, setBlendCategories] = useState(["Fossil", "Fossil"]);
    const [showErrors, setShowErrors] = useState(false);
    const [fuelCodeError, setFuelCodeError] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isClassifcationChanged, setIsClassifcationChanged] = useState(false);

    const [expandedMainPanel, setExpandedMainPanel] = useState("panel0");

    const getCslipMethaneError = (fuel) => {
        const metadata = (fuel?.metadata || []).map((item) => ({
            ...item,
            normalizedKey: (item.metaKey ?? item.MetaKey ?? "").toUpperCase(),
            value: item.metaValue ?? item.MetaValue,
            metaType: item.metaType ?? item.MetaType,
        }));
        const isApplicable = (value) => {
            const normalizedValue = String(value ?? "").trim().toLowerCase();
            return value === true || normalizedValue === "1" || normalizedValue === "true" || normalizedValue === "applicable";
        };
        const cslipEntries = metadata.filter((item) => item.normalizedKey.endsWith("CSLIP"));
        if (!cslipEntries.length && isApplicable(fuel?.cslip)) {
            cslipEntries.push({ normalizedKey: "CSLIP", value: fuel.cslip });
        }

        const methaneFactors = metadata.filter((item) =>
            item.normalizedKey === "EF|CH4" || item.normalizedKey.endsWith("|EF|CH4")
        );
        const editableMethaneFactors = methaneFactors.filter((item) =>
            item.metaType == null || Number(item.metaType) === 1
        );

        // Specific CSLIP keys (for example UKCSLIP) own their matching ETS factor.
        // The generic CSLIP rule checks all remaining methane factors, so future
        // factor metadata is covered without adding it to a fixed key list.
        const specificFactorKeys = new Set([
            "UKETS|EF|CH4",
            ...cslipEntries
                .map((item) => item.normalizedKey)
                .filter((key) => key !== "CSLIP")
                .map((key) => `${key.slice(0, -"CSLIP".length)}ETS|EF|CH4`),
        ]);
        const invalidCslip = cslipEntries.find((cslip) => {
            if (!isApplicable(cslip.value)) return false;

            const relatedFactors = cslip.normalizedKey === "CSLIP"
                ? editableMethaneFactors.filter((item) => !specificFactorKeys.has(item.normalizedKey))
                : editableMethaneFactors.filter((item) =>
                    item.normalizedKey === `${cslip.normalizedKey.slice(0, -"CSLIP".length)}ETS|EF|CH4`
                );

            return relatedFactors.some((item) =>
                item.value === null || item.value === undefined || item.value === "" || Number(item.value) !== 0
            );
        });

        if (!invalidCslip) return null;

        const fuelType = fuel?.fuelType || fuel?.code || fuel?.name || fuel?.fuelName || "Fuel";
        return `EF_CH4 for ${fuelType} must be 0 if CSlip is applicable. Please revise data`;
    };

    const createFuel = ({ vesselMstId, categoryType = "Fossil" } = {}) => ({
        fuelRegId: null,
        vesselMstId,
        categoryType,
        code: "",
        name: "",
        fuelType: "",
        fuelName: "",
        isEditable: true,
        childFuels: [],
        metadata: [],
    });

    const [fuelState, setFuelState] = useState(createFuel({ vesselMstId }));

    const [blendChildFuels, setBlendChildFuels] = useState([createFuel({ vesselMstId }), createFuel({ vesselMstId })]);

    const isClassificationDisabled = fuelRegId > 0 && (classification === "Blend" || fuelState.code === "ETHA");
        
    useEffect(() => {
        const loadBaseFuels = async () => {
            try {
                const { data } = await axiosHttp.get("/api/FuelRegistry/base");
                setBaseFuels(data?.data ?? []);
            } catch (err) {
                console.error("Failed to load base fuels", err);
                setBaseFuels([]);
            } finally {
                setIsBaseFuelsLoaded(true);
            }
        };

        const fetchFuelRegistry = async (id) => {
            try {
                const { data } = await axiosHttp.get("/api/FuelRegistry/" + id);

                if (data?.data) {
                    const existingFuelReg = data.data;
                    const updates = {
						fuelRegId: existingFuelReg.fuelRegId,
						parentFuelRegId: existingFuelReg.parentFuelRegId,
						code: existingFuelReg.code,
						name: existingFuelReg.fuelName,
						vesselMstId: existingFuelReg.vesselMstId,
						categoryType: existingFuelReg.categoryType,
						fuelType: existingFuelReg.fuelType,
						fuelName: existingFuelReg.fuelName,
						isEditable: existingFuelReg.isEditable,
						metadata: existingFuelReg.metadata?.map((m) => ({
							...m,
							originalValue: m.metaValue,
						})),
					};
                    setFuelState((prev) => ({
                        ...prev,
                        ...updates,
                    }));
                    setClassification(existingFuelReg.categoryType);
                    if (existingFuelReg.categoryType === FUEL_CATEGORIES.BLEND) {

                        setClassification(existingFuelReg.categoryType);

                        if (existingFuelReg.childFuels?.length) {
                            setBlendChildFuels(
                                existingFuelReg.childFuels.map((f) => ({
                                    ...f,
                                    code: f.fuelType,
                                    name: f.fuelName,
                                    childFuels: f.childFuels ?? []
                                }))
                            );

                            setBlendCategories(existingFuelReg.childFuels.map((f) => f.categoryType || "Fossil"));
                        }

                    }
                }
            } catch (err) {
                console.error("Failed to Fuel Reg", err);
            } finally {
            }
        };

        loadBaseFuels();
        if (fuelRegId) {
            fetchFuelRegistry(fuelRegId);
        }
    }, [fuelRegId]);

    useEffect(() => {
        if (
            classification !== FUEL_CATEGORIES.BLEND ||
            fuelRegId ||
            hasRequestedBlendCode.current
        ) {
            return;
        }

        hasRequestedBlendCode.current = true;
        const loadBlendCode = async () => {
            try {
                const response = await axiosHttp.get("/api/FuelRegistry/GetBlendCode", {
                    params: { vesselMstId },
                });
                const result = response?.data?.data ?? response?.data;
                const code = typeof result === "string"
                    ? result
                    : result?.blendCode ?? result?.BlendCode ?? result?.code ?? result?.Code;

                if (typeof code !== "string" || !code.trim()) {
                    hasRequestedBlendCode.current = false;
                    console.error("Failed to load Blend Code: API returned no code");
                    return;
                }

                setBlendCode(code);
                setFuelState((prev) => {
                    if (prev.categoryType !== FUEL_CATEGORIES.BLEND) return prev;

                    const metadata = [...(prev.metadata ?? [])];
                    const existingIndex = metadata.findIndex((item) => item.metaKey === "BLEND|CODE");
                    const blendCodeMetadata = {
                        ...(existingIndex >= 0 ? metadata[existingIndex] : {}),
                        metaKey: "BLEND|CODE",
                        metaValue: code,
                        metaType: 1,
                    };

                    if (existingIndex >= 0) {
                        metadata[existingIndex] = blendCodeMetadata;
                        return { ...prev, metadata };
                    }

                    return { ...prev, metadata: [...metadata, blendCodeMetadata] };
                });
            } catch (err) {
                hasRequestedBlendCode.current = false;
                console.error("Failed to load Blend Code", err);
            }
        };

        loadBlendCode();
    }, [classification, fuelRegId, vesselMstId]);

    const filteredBaseFuels = () => {
        if (fuelRegId) {
            return _.filter(baseFuels);
        }
        return _.filter(baseFuels, (x) => x.categoryType === classification);
    };

    const handleClassificationChange = (newClassification) => {
        setIsClassifcationChanged(true);
        if (fuelRegId > 0 && classification) return;

        setFuelState((prev) => ({
            ...prev,
            categoryType: newClassification,
            code: "",
            name: "",
            fuelType: "",
            fuelName: "",
            metadata: newClassification === FUEL_CATEGORIES.BLEND
                ? [
                    ...(prev.metadata ?? []).filter((item) => item.metaKey !== "BLEND|CODE"),
                    ...(blendCode
                        ? [{ metaKey: "BLEND|CODE", metaValue: blendCode, metaType: 1 }]
                        : []),
                ]
                : [],
        }));

        if (newClassification === FUEL_CATEGORIES.BLEND) {
            setBlendChildFuels([vesselMstId, vesselMstId]);
            setBlendCategories(["Fossil", "Fossil"]);
        } else {
            setBlendChildFuels([]);
            setBlendCategories([]);
        }

        setShowErrors(false);
        setFuelCodeError(false);
    };


    const hasValidationErrors = () => {
		if (!fuelState.fuelType && classification !== FUEL_CATEGORIES.BLEND) {
			setFuelCodeError(true);
			return true;
		}

		if (classification === FUEL_CATEGORIES.FOSSIL) {
			return false;
		}

		// SINGLE e-fuel EU mandatory
		if (classification === FUEL_CATEGORIES.E_FUEL) {
			const euMeta = fuelState.metadata?.find((m) => m.metaKey === "EU|VALUE");
			const euVal = euMeta?.metaValue;

			if (!euVal || euVal.toString().trim() === "") {
				return true;
			}
		}

		const allFields = fuelRegistryFieldConfigs.flat();

		if (classification !== "Blend") {
			for (const field of allFields) {
				if (!field.required || !field.display) continue;
				if (![FUEL_CATEGORIES.BIOFUEL, FUEL_CATEGORIES.OTHER].includes(classification) && ["QUANTITY|MT", "ENERGY|CONTENT|MJ"].includes(field.metaKey)) {
					continue;
				}

				const value = field.isMeta ? fuelState.metadata?.find((m) => m.metaKey === field.metaKey)?.metaValue : fuelState[field.fieldName];

				if (!value || value.toString().trim() === "") {
					return true;
				}
			}
		}

		if (classification === FUEL_CATEGORIES.BIOFUEL) {
			const qty = fuelState.metadata?.find((m) => m.metaKey === "QUANTITY|MT")?.metaValue;
			const energy = fuelState.metadata?.find((m) => m.metaKey === "ENERGY|CONTENT|MJ")?.metaValue;
			const eValue = fuelState.metadata?.find((m) => m.metaKey === "E|VALUE|GCO2EQ|MJ")?.metaValue;

			if (!qty || !energy || !eValue) {
				return true;
			}
			return false;
		}

		const parentFields = _.flatten(blendFuelRegistryFieldConfigs);
		if (classification === FUEL_CATEGORIES.BLEND) {
			for (let field of parentFields) {
				if (!field.required || !field.display) continue;

				const value = field.isMeta ? fuelState.metadata?.find((m) => m.metaKey === field.metaKey)?.metaValue : fuelState[field.fieldName];

				if (!value || value.toString().trim() === "") {
					return true;
				}
			}
			for (let child of blendChildFuels) {
				if (!child.fuelType) return true;

				const hasMissingField = BLENDREQUIREDFIELDS.some((metaKey) => {
					if (child.categoryType === FUEL_CATEGORIES.FOSSIL && ["E|VALUE|GCO2EQ|MJ", "ENERGY|CONTENT|MJ"].includes(metaKey)) {
						return false;
					}
					if (child.categoryType === FUEL_CATEGORIES.E_FUEL && ["ENERGY|CONTENT|MJ"].includes(metaKey)) {
						return false;
					}
					if (child.categoryType === FUEL_CATEGORIES.E_FUEL) {
						const euMeta = child.metadata?.find((m) => m.metaKey?.includes("EU|VALUE"));

						const euVal = euMeta?.metaValue;
						if (!euVal || euVal.toString().trim() === "") {
							return true; //
						}
					}

					const value = child.metadata?.find((m) => m.metaKey === metaKey)?.metaValue;
					return !value || value.toString().trim() === "";
				});
				if (hasMissingField) return true;
			}
		}
		setFuelCodeError(false);
		return false;
	};

    const getInvalidBlendChildIndexes = () => {
        if (classification !== FUEL_CATEGORIES.BLEND) return [];

        return blendChildFuels
            .map((child, index) => {

                if (!child.fuelType) return index;

                const hasMissingField = BLENDREQUIREDFIELDS.some((metaKey) => {
                    if (child.categoryType === FUEL_CATEGORIES.FOSSIL && ["E|VALUE|GCO2EQ|MJ", "ENERGY|CONTENT|MJ"].includes(metaKey)) {
                        return false
                    }
                    if (child.categoryType === FUEL_CATEGORIES.E_FUEL && ["ENERGY|CONTENT|MJ"].includes(metaKey)) {
                        return false
                    }
                    const value = child.metadata?.find((m) => m.metaKey === metaKey)?.metaValue;
                    return !value || value.toString().trim() === "";
                });

                return hasMissingField ? index : null;
            })
            .filter((i) => i !== null);
    };

    const saveFuel = async () => {
        if (isSaving) return;
        
        const fuelsToValidate = classification === FUEL_CATEGORIES.BLEND ? blendChildFuels : [fuelState];
        const cslipMethaneError = fuelsToValidate.map(getCslipMethaneError).find(Boolean);
        if (cslipMethaneError) {
            emitToasterMsg("error", "Fuel Registry", cslipMethaneError);
            return;
        }
        
        setShowErrors(true);

        if (hasValidationErrors()) {
            return;
        }

        const invalidChildIndexes = getInvalidBlendChildIndexes();
        if (invalidChildIndexes.length > 0) return;

        const isEdit = Boolean(fuelState?.fuelRegId);

        try {
            setIsSaving(true);

            if (classification === "Blend") {
                await AddFuel(buildBlendPayload(), isEdit);
            } else {
                await AddFuel(buildFossilPayload(), isEdit);               
            }
        } finally {
            setIsSaving(false);
        }
    };

    const AddFuel = async (payload, isEdit) => {
        try {
            const response = await callApi({
                method: "POST",
                url: "/api/FuelRegistry/SetVesselFuel",
                data: payload,
            });
            if (response) {
                emitToasterMsg("success", "Fuel Registry", isEdit ? "Fuel updated successfully" : "Fuel added successfully");
                onSuccess?.();
                return true;
            } else {
                emitToasterMsg("error", "Fuel Registry", response?.errorMessage || "Failed to add fuel");
                onSuccess?.();
                return false;
            }
        } catch (error) {
            emitToasterMsg("error", "Fuel Registry", error?.errorMessage || "Something went wrong while saving fuel");
            return false;
        }
    };

    const buildFossilPayload = () => ({
        ...fuelState,
        _tempId: String(fuelState.fuelRegId) ?? crypto.randomUUID(),
        categoryType: classification,
        code: fuelState.fuelType,
        name: fuelState.fuelName,
        vesselMstId,
        metadata: (fuelState.metadata || []).map(m => ({
            ...m,
            isOverWritten: Boolean(m.isOverWritten),
        })),
    });

    const getBlendParentMetadata = () => {
		return (fuelState.metadata || []).filter((m) => BLEND_PARENT_META_KEYS.includes(m.metaKey));
	};
    const buildBlendPayload = () => {
        const blendCodeMetadata = fuelState.metadata?.find((m) => m.metaKey === "BLEND|CODE");
        const parentBdnMetadata = fuelState.metadata?.find((m) => m.metaKey === "BDN|ID");

        return {
            fuelRegId: fuelState.fuelRegId ?? null,
            _tempId: String(fuelState.fuelRegId) ?? crypto.randomUUID(),
            categoryType: "Blend",
            code: fuelState.fuelType,
            name: fuelState.fuelName,
            vesselMstId,
            fuelType: fuelState.fuelType,
            fuelName: fuelState.fuelName,
            metadata: getBlendParentMetadata(),
            lcv: null,
            co2eq: null,
            efCo2: null,
            efCh4: null,
            efN2o: null,
            rwd: null,
            cslip: false,
            childFuels: blendChildFuels
                .filter((f) => f?.fuelType)
                .map((f) => ({
                    ...f,
                    fuelRegId: f.fuelRegId ?? null,
                    _tempId: String(f.fuelRegId) ?? crypto.randomUUID(),
                    code: f.fuelType,
                    name: f.fuelName,
                    metadata: [
                        ...(f.metadata ?? []).filter((m) => m.metaKey !== "BLEND|CODE" && m.metaKey !== "BDN|ID"),
                        ...(parentBdnMetadata ? [{ ...parentBdnMetadata }] : []),
                        ...(blendCodeMetadata ? [{ ...blendCodeMetadata }] : []),
                    ],
                })),
        };
    };

    const handleMainChange = (panel) => (_, expanded) => setExpandedMainPanel(expanded ? panel : false);

    const removeBlendFuel = (index) => {
        setBlendChildFuels((prev) => prev.filter((_, i) => i !== index));
        setBlendCategories((prev) => prev.filter((_, i) => i !== index));
    };

    const renderNonBlendFuel = () =>
        isBaseFuelsLoaded &&
        classification !== "Blend" && (
            <NonBlendFuelRegistryForm
                filteredBaseFuels={filteredBaseFuels()}
                fuelState={fuelState}
                onFuelChange={handleSingleFuelChange}
                showErrors={showErrors}
                classification={classification}
                isBlendChild={false}
                classificationChanged={isClassifcationChanged}
            />
        );

    const handleParentCo2Update = (childFuels) => {
        const parentCo2s = calcParentCo2s(childFuels);

        setFuelState((prev) => {
            const metadata = [...(prev.metadata || [])];

            Object.entries(parentCo2s).forEach(([key, value]) => {
                const idx = metadata.findIndex((m) => m.metaKey === key);
                if (idx >= 0) {
                    metadata[idx] = { ...metadata[idx], metaValue: value };
                } else {
                    metadata.push({ metaKey: key, metaValue: value });
                }
            });

            return { ...prev, metadata };
        });
    };
    const handleParentMassUpdate = (childFuels) => {
        const parentMass = calcParentMass(childFuels);

        setFuelState((prev) => {
            const metadata = [...(prev.metadata || [])];

            Object.entries(parentMass).forEach(([key, value]) => {
                const idx = metadata.findIndex((m) => m.metaKey === key);
                if (idx >= 0) {
                    metadata[idx] = { ...metadata[idx], metaValue: value };
                } else {
                    metadata.push({ metaKey: key, metaValue: value });
                }
            });

            return { ...prev, metadata };
        });
    };
    const handleSingleFuelChange = (updater) => {
        setFuelState((prev) => {
            const next = typeof updater === "function" ? updater(prev) : updater;
            const updatedFuel = {
                ...next,
                categoryType: classification,
            };
            let fuels = [updatedFuel];

            fuels = calcLcvs(fuels);
            fuels = calcCo2Eq(fuels);
            return fuels[0];
        });
    };

    useEffect(() => {
        if (classification === "Biofuel") {
            setFuelState(prev => ({
                ...prev,
                metadata: prev.metadata?.map(m =>
                    (m.metaKey === "QUANTITY|MT" || m.metaKey === "ENERGY|CONTENT|MJ"|| m.metaKey==="E|VALUE|GCO2EQ|MJ" ) &&
                        m.metaValue === null
                        ? { ...m, metaValue: "" }
                        : m
                ),
            }))
        }
        if (classification === "e-Fuel") {
            setFuelState(prev => ({
                ...prev,
                metadata: prev.metadata?.map(m =>
                    (m.metaKey==="E|VALUE|GCO2EQ|MJ" ) &&
                        m.metaValue === null
                        ? { ...m, metaValue: "" }
                        : m
                ),
            }))
        }
    }, [classification]);

    return (
        <div style={{ padding: "1rem" }}>
            <MDBRow className="mb-4">
                <MDBCol md="3">
                    <div>Fuel Classification</div>
                    <AbsDropDown
                        options={FUEL_CLASSIFICATIONS}
                        selected={classification}
                        disabled={isClassificationDisabled}
                        handleChange={(o) => {
                            const value = o?.value ?? "";
                            setClassification(value);
                            handleClassificationChange(value);
                        }}
                    />
                </MDBCol>
                {classification === "Blend" && (
                    <MDBCol md="12">
                        <NonBlendFuelRegistryForm
                            filteredBaseFuels={filteredBaseFuels()}
                            fuelState={fuelState}
                            onFuelChange={setFuelState}
                            showErrors={showErrors}
                            classification={"Blend"}
                            isBlendChild={false}
                        />
                    </MDBCol>
                )}
            </MDBRow>

            <MDBRow className="mb-4">
                <MDBCol md="12">{renderNonBlendFuel()}</MDBCol>
            </MDBRow>
            <MDBRow className="mb-4">
                <MDBCol md="12">
                    {isBaseFuelsLoaded && classification === "Blend" && (
                        <>
                            {blendCategories.map((category, index) => {
                                const invalidIndexes = getInvalidBlendChildIndexes();
                                const isInvalid = invalidIndexes.includes(index);
                                return (
                                    <BlendChildFuelAccordion
                                        key={`${index}-${category}`}
                                        index={index}
                                        category={category}
                                        blendChildFuels={blendChildFuels}
                                        setBlendChildFuels={setBlendChildFuels}
                                        blendCategories={blendCategories}
                                        setBlendCategories={setBlendCategories}
                                        expandedMainPanel={expandedMainPanel}
                                        handleMainChange={handleMainChange}
                                        baseFuels={baseFuels}
                                        vesselMstId={vesselMstId}
                                        removeBlendFuel={removeBlendFuel}
                                        onparentCo2Update={handleParentCo2Update}
                                        onparentMassUpdate={handleParentMassUpdate}
                                        showErrors={showErrors}
                                        createFuel={createFuel}
                                        isInvalid={isInvalid}
                                        fuelRegId={fuelRegId}
                                    />
                                );
                            })}
                        </>
                    )}
                </MDBCol>
            </MDBRow>
            <MDBRow>
                {classification === "Blend" && (
                    <MDBCol md="4">
                        <div className="d-flex mr-3 mb-2">
                            <Button
                                onClick={() => {
                                    setBlendChildFuels((prev) => [...prev, createFuel(vesselMstId)]);
                                    setBlendCategories((prev) => [...prev, "Fossil"]);
                                }}
                            >
                                + Add Another Fuel
                            </Button>
                        </div>
                    </MDBCol>
                )}
            </MDBRow>
            <MDBRow className="mb-4">
                <MDBCol md="4">
                    <div className="d-flex mr-3">
                        <button type="button" className="save-button mr-2" onClick={saveFuel} disabled={isSaving}>
                            {isSaving ? "Saving..." : fuelState.fuelRegId ? "Update Fuel" : "Add Fuel"}
                        </button>
                    </div>
                </MDBCol>
            </MDBRow>
        </div>
    );
};

export default FuelRegistryForm;
